import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft } from 'lucide-react';
import BrandLogo from '../../components/common/BrandLogo';
import { toast } from 'sonner';

const CustomerRegister = () => {
  const { registerCustomer } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    organization_name: '', contact_person: '', email: '',
    password: '', phone: '',
    street: '', house_number: '', postal_code: '', city: '', country: 'Deutschland',
    name: ''
  });
  const [loading, setLoading] = useState(false);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await registerCustomer({ ...form, name: form.contact_person });
      toast.success('Registrierung erfolgreich');
      navigate('/customer');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-xl">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" /> Zurück zur Anmeldung
        </Link>

        <div className="bg-white border border-slate-200 rounded-md p-8 card-subtle">
          <div className="mb-6">
            <BrandLogo height={40} />
          </div>

          <h1 className="font-heading text-2xl font-semibold text-slate-900 tracking-tight">Als Kunde registrieren</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">Erstellen Sie ein Konto, um Dolmetschtermine zu buchen.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="customer-register-form">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Organisation / Firma / Behörde *</label>
              <input required value={form.organization_name} onChange={(e) => upd('organization_name', e.target.value)} data-testid="reg-org-input" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Ansprechpartner *</label>
              <input required value={form.contact_person} onChange={(e) => upd('contact_person', e.target.value)} data-testid="reg-contact-input" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">E-Mail *</label>
                <input required type="email" value={form.email} onChange={(e) => upd('email', e.target.value)} data-testid="reg-email-input" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Telefon</label>
                <input value={form.phone} onChange={(e) => upd('phone', e.target.value)} data-testid="reg-phone-input" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Adresse (für Rechnung)</label>
              <div className="grid grid-cols-[1fr_100px] gap-2 mb-2">
                <input required value={form.street} onChange={(e) => upd('street', e.target.value)} data-testid="reg-street-input" placeholder="Straße" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                <input required value={form.house_number} onChange={(e) => upd('house_number', e.target.value)} data-testid="reg-housenr-input" placeholder="Hausnr." className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
              <div className="grid grid-cols-[120px_1fr] gap-2">
                <input required value={form.postal_code} onChange={(e) => upd('postal_code', e.target.value)} data-testid="reg-plz-input" placeholder="PLZ" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                <input required value={form.city} onChange={(e) => upd('city', e.target.value)} data-testid="reg-city-input" placeholder="Ort" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Passwort *</label>
              <input required type="password" minLength={6} value={form.password} onChange={(e) => upd('password', e.target.value)} data-testid="reg-password-input" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
            </div>

            <button type="submit" disabled={loading} data-testid="reg-submit-btn" className="w-full bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded-md px-4 py-2.5 text-sm transition-colors disabled:opacity-50">
              {loading ? 'Registrieren...' : 'Konto erstellen'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CustomerRegister;
